import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import FormatResourceStatus from "../../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { UpsellApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import UpsellForm from "../New/components/UpsellForm";

interface MatchParams {
	upsellId: string;
}

const OrganizationMenuUpsellsEditPage = (): JSX.Element => {
	const [upsell, setUpsell] = useState<UpsellApi | undefined>();
	const [showLogs, setShowLogs] = useState(false);
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const isMobile = useWindowSize().isMobile;
	const { upsellId } = useParams<MatchParams>();
	const { addFlash, addSuccessFlash } = useFlash();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const include = "expressions,expressions.conditions,items,items.item";

	useEffect(() => {
		fetchUpsell();
	}, []);

	const fetchUpsell = async () => {
		try {
			const res = await apiOrganization.getUpsell(Number(upsellId), { include });
			setUpsell(res);
			handleChangeTabTitle(`${res.name} | ${t("modules.upsell.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const removeUpsell = async () => {
		if (!upsell) return;

		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});
			await apiOrganization.removeUpsell(upsell.id!);
			addSuccessFlash(t("lib:common.flash.removed"));
			history.push(`/${organization.id}/menu/upsells`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const restoreUpsell = async () => {
		if (!upsell) return;

		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.activate"),
			});

			const res = await api.organization().restoreDiscount(upsell.id!, { include });
			addSuccessFlash(t("lib:common.flash.completed"));
			setUpsell(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const disableUpsell = async () => {
		if (!upsell) return;

		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.disable"),
			});
			await apiOrganization.disableUpsell(upsell.id!);
			addSuccessFlash(t("lib:common.flash.completed"));
			history.push(`/${organization.id}/menu/upsells`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!upsell) {
		return <LoadingContainer />;
	}

	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(!showLogs),
			variant: "light",
		},
		{
			title: t("lib:common.action.remove"),
			action: () => removeUpsell(),
			dropdown: true,
			hide: upsell.status === "DELETED",
		},
		{
			title: t("lib:common.action.disable"),
			action: () => disableUpsell(),
			dropdown: true,
			hide: upsell.status !== "ENABLED",
		},
		{
			title: t("lib:common.action.activate"),
			action: () => restoreUpsell(),
			dropdown: true,
			hide: !(upsell.status === "DELETED" || upsell.status === "DISABLED"),
		},
		{
			title: t("lib:common.action.copy"),
			action: () => {
				history.push({
					pathname: `${location.pathname.split("/").slice(0, -1).join("/")}/new`,
					state: {
						upsell: {
							...upsell,
							id: null,
							name: `${t("common.word.copy")} ${upsell.name}`,
						},
					},
				});
			},
			dropdown: true,
		},
	];
	const mobileActions: MobileActionProps[] = buttons.map((button) => ({
		action: button.action,
		hide: button.hide,
		path: button.path,
		state: button.state,
		title: button.title ?? "",
	}));

	const renderTitle = () => (
		<>
			{`${t("modules.upsell.header.title")} "${upsell.name}"`}
			<FormatResourceStatus status={upsell.status} />
		</>
	);

	return (
		<>
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} />
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={upsell.id!}
					resourceType="UPSELL"
					onHide={() => setShowLogs(!showLogs)}
					organizationId={organization.id}
				/>
			)}
			<UpsellForm upsell={upsell} />
		</>
	);
};

export default OrganizationMenuUpsellsEditPage;
